(() => {
  if (customElements.get('custom-cart-drawer')) return;

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CustomCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.pending = 0;
      this.ownRequestStarts = [];
      this.renderId = 0;
      this.lastFocus = null;
      this.savedOverflow = '';

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      // Scroll doesn't bubble; capture reaches carousel tracks that get re-rendered.
      this.addEventListener('scroll', this.onScroll, true);
      // Capture phase so cart links reach this drawer before Slide Cart's own listeners.
      window.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);

      this.watchCartRequests();
      this.watchSlideCart();
      window.CustomCartDrawer = this;

      if (new URLSearchParams(window.location.search).get('cart_drawer') === 'open') this.open();
    }

    disconnectedCallback() {
      this.close();
      window.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      this.requestObserver?.disconnect();
      this.slideCartObserver?.disconnect();
      this.bodyObserver?.disconnect();
      window.clearTimeout(this.refreshTimer);
      if (window.CustomCartDrawer === this) delete window.CustomCartDrawer;
    }

    get container() {
      return this.querySelector('[data-ccd-container]');
    }

    get panel() {
      return this.querySelector('.custom-cart-drawer-panel');
    }

    get content() {
      return this.querySelector('[data-ccd-content]');
    }

    isActive() {
      return document.documentElement.dataset.customCartDrawer === 'on';
    }

    // Sections are rendered against the store root: product URLs answer JSON requests with
    // the product's own JSON and ignore ?sections=.
    get sectionsUrl() {
      return (this.dataset.ccdRootUrl || '/').replace(/\/?$/, '/');
    }

    get isOpen() {
      return Boolean(this.container?.classList.contains('is-open'));
    }

    open() {
      if (!this.container || this.isOpen || !this.isActive()) return;
      this.lastFocus = document.activeElement;
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.container.classList.add('is-open');
      this.panel.focus({ preventScroll: true });
      this.syncCarousels();
      this.refresh();
      this.emit('custom-cart-drawer:open');
    }

    close() {
      if (!this.isOpen) return;
      this.container.classList.remove('is-open');
      document.documentElement.style.overflow = this.savedOverflow;
      this.showError('');
      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
      this.lastFocus = null;
      this.emit('custom-cart-drawer:close');
    }

    async refresh() {
      const renderId = ++this.renderId;
      try {
        const url = `${this.sectionsUrl}?sections=${encodeURIComponent(this.dataset.sectionId)}`;
        const response = await fetch(url);
        if (!response.ok) return;
        const sections = await response.json();
        if (renderId === this.renderId) this.render(sections[this.dataset.sectionId]);
      } catch {
        // Keep what is on screen; the next cart action re-renders it anyway.
      }
    }

    // Lines are addressed by key, not index, so a cart changed by another script can't shift the target.
    changeLine(key, quantity, quiet) {
      const body = JSON.stringify({
        id: key,
        quantity: Math.max(0, Number.parseInt(quantity, 10) || 0),
        sections: [this.dataset.sectionId],
        sections_url: this.sectionsUrl,
      });
      return this.send(`${this.dataset.ccdChangeUrl}.js`, body, { 'Content-Type': 'application/json' }, quiet);
    }

    addFromForm(form) {
      const body = new FormData(form);
      body.append('sections', this.dataset.sectionId);
      body.append('sections_url', this.sectionsUrl);
      return this.send(`${this.dataset.ccdAddUrl}.js`, body, { 'X-Requested-With': 'XMLHttpRequest' });
    }

    addVariant(variantId, quantity, quiet) {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', quantity);
      body.append('sections', this.dataset.sectionId);
      body.append('sections_url', this.sectionsUrl);
      return this.send(`${this.dataset.ccdAddUrl}.js`, body, { 'X-Requested-With': 'XMLHttpRequest' }, quiet);
    }

    // quiet = the shopper didn't ask for this, so don't put an error in front of them.
    async send(url, body, headers, quiet) {
      this.pending += 1;
      this.ownRequestStarts.push(performance.now());
      this.setBusy(true);
      this.showError('');
      // Any refresh still in flight predates this change and must not overwrite it.
      const renderId = ++this.renderId;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json', ...headers },
          body,
        });
        const data = await response.json();

        if (!response.ok) {
          if (!quiet) this.showError(data.description || data.message || this.dataset.errorText);
          return null;
        }

        const html = data.sections?.[this.dataset.sectionId];
        if (html && renderId === this.renderId) {
          this.render(html);
        } else {
          await this.refresh();
        }
        this.emit('custom-cart-drawer:updated', { response: data });
        return data;
      } catch {
        if (!quiet) this.showError(this.dataset.errorText);
        return null;
      } finally {
        this.pending -= 1;
        this.setBusy(false);
      }
    }

    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`#${CSS.escape(this.id)} [data-ccd-content]`);
      const current = this.content;
      if (!fresh || !current) return;

      const focusSelector = this.focusSelectorFor(document.activeElement);
      const scrollTop = current.querySelector('.custom-cart-drawer-body')?.scrollTop || 0;

      current.innerHTML = fresh.innerHTML;
      current.dataset.ccdCount = fresh.dataset.ccdCount;
      current.dataset.ccdTotal = fresh.dataset.ccdTotal;
      current.dataset.ccdGiftLine = fresh.dataset.ccdGiftLine;
      current.dataset.ccdGiftQuantity = fresh.dataset.ccdGiftQuantity;
      current.dataset.ccdGiftExtra = fresh.dataset.ccdGiftExtra;

      const body = current.querySelector('.custom-cart-drawer-body');
      if (body) body.scrollTop = scrollTop;
      if (focusSelector && this.isOpen) {
        (this.querySelector(focusSelector) || this.panel).focus({ preventScroll: true });
      }

      this.syncCarousels();
      this.updateHeaderCount(Number(fresh.dataset.ccdCount) || 0);
      this.emit('custom-cart-drawer:render');
    }

    focusSelectorFor(element) {
      if (!element || !this.contains(element)) return null;
      if (element.dataset.ccdAction) {
        return `[data-ccd-key="${CSS.escape(element.dataset.ccdKey)}"][data-ccd-action="${element.dataset.ccdAction}"]`;
      }
      const form = element.closest('form[id]');
      if (form) return `#${CSS.escape(form.id)} ${element.tagName.toLowerCase()}`;
      return '.custom-cart-drawer-panel';
    }

    // Mirrors theme.js AjaxCart so the header bubble stays in step with the drawer.
    updateHeaderCount(count) {
      document.querySelectorAll('.site-header__cart-count').forEach((element) => {
        element.textContent = count;
        element.classList.toggle('hide', count === 0);
      });
      document.querySelectorAll('.site-header__cart').forEach((element) => {
        element.classList.toggle('has-items', count > 0);
      });
    }

    setBusy(busy) {
      this.panel?.setAttribute('aria-busy', String(busy && this.pending > 0));
    }

    showError(message) {
      const element = this.querySelector('[data-ccd-error]');
      if (!element) return;
      element.textContent = message || '';
      element.hidden = !message;
    }

    // Other scripts (theme AjaxCart, free-gift and bundle apps) change the cart too;
    // re-render after any cart mutation this drawer didn't make itself.
    watchCartRequests() {
      if (!('PerformanceObserver' in window)) return;
      this.requestObserver = new PerformanceObserver((list) => {
        const external = list.getEntries().some((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
          if (!CART_MUTATION.test(new URL(entry.name).pathname)) return false;
          const own = this.ownRequestStarts.findIndex((start) => Math.abs(entry.startTime - start) < 50);
          if (own === -1) return true;
          this.ownRequestStarts.splice(own, 1);
          return false;
        });
        if (external) this.scheduleRefresh();
      });
      this.requestObserver.observe({ type: 'resource' });
    }

    scheduleRefresh() {
      if (!this.isActive()) return;
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), 150);
    }

    // The Slide Cart app embed is still enabled and opens itself on some add-to-cart
    // paths; hand those opens to this drawer. Remove once the embed is switched off.
    watchSlideCart() {
      const observe = (root) => {
        this.slideCartObserver = new MutationObserver(() => {
          if (!this.isActive() || !root.querySelector('.slidecarthq.open')) return;
          window.SLIDECART_CLOSE?.();
          this.open();
        });
        this.slideCartObserver.observe(root, { subtree: true, attributes: true, attributeFilter: ['class'] });
      };

      const existing = document.getElementById('slidecarthq');
      if (existing) {
        observe(existing);
        return;
      }
      this.bodyObserver = new MutationObserver(() => {
        const root = document.getElementById('slidecarthq');
        if (!root) return;
        this.bodyObserver.disconnect();
        observe(root);
      });
      this.bodyObserver.observe(document.body, { childList: true });
    }

    trapFocus(event) {
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((element) => element.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail: { drawer: this, ...detail } }));
    }

    onClick(event) {
      const target = event.target;

      if (target.closest('[data-ccd-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const action = target.closest('a[data-ccd-action]');
      if (action) {
        event.preventDefault();
        this.changeLine(action.dataset.ccdKey, action.dataset.ccdQuantity);
        return;
      }

      const prev = target.closest('[data-ccd-carousel-prev]');
      const next = target.closest('[data-ccd-carousel-next]');
      if (prev || next) this.stepCarousel((prev || next).closest('[data-ccd-carousel]'), prev ? -1 : 1);
    }

    updateVariantPrice(select) {
      const option = select.selectedOptions[0];
      const card = select.closest('form')?.parentElement;
      if (!option || !card) return;
      const price = card.querySelector('[data-ccd-price]');
      const compare = card.querySelector('[data-ccd-compare]');
      if (price) price.textContent = option.dataset.price || '';
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
    }

    syncCarousels() {
      for (const carousel of this.querySelectorAll('[data-ccd-carousel]')) this.syncCarousel(carousel);
    }

    syncCarousel(carousel) {
      const track = carousel.querySelector('[data-ccd-carousel-track]');
      if (!track?.clientWidth) return;
      const thumb = carousel.querySelector('[data-ccd-carousel-thumb]');
      const prev = carousel.querySelector('[data-ccd-carousel-prev]');
      const next = carousel.querySelector('[data-ccd-carousel-next]');
      const max = track.scrollWidth - track.clientWidth;
      const scrollable = max > 1;

      if (prev) {
        prev.parentElement.hidden = !scrollable;
        prev.disabled = track.scrollLeft <= 1;
      }
      if (next) next.disabled = track.scrollLeft >= max - 1;
      if (thumb) {
        thumb.parentElement.hidden = !scrollable;
        thumb.style.setProperty('--thumb-size', `${(track.clientWidth / track.scrollWidth) * 100}%`);
        thumb.style.setProperty('--thumb-offset', `${(track.scrollLeft / track.scrollWidth) * 100}%`);
      }
    }

    stepCarousel(carousel, direction) {
      const track = carousel?.querySelector('[data-ccd-carousel-track]');
      const card = track?.firstElementChild;
      if (!card) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({
        left: direction * (card.getBoundingClientRect().width + gap),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    onScroll(event) {
      const carousel = event.target.closest?.('[data-ccd-carousel]');
      if (carousel) this.syncCarousel(carousel);
    }

    onResize() {
      if (this.isOpen) this.syncCarousels();
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-ccd-variant-select]')) {
        this.updateVariantPrice(target);
        return;
      }
      if (!target.matches('[data-ccd-action="set"]')) return;
      // Enter already sent this value; the re-render then removes the input, which fires change again.
      if (target.dataset.submittedValue === target.value) return;
      this.changeLine(target.dataset.ccdKey, target.value);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-ccd-add]');
      if (!form) return;
      event.preventDefault();

      // Don't reorder: FormData has to be read before anything in the form is disabled.
      const request = this.addFromForm(form);
      const button = form.querySelector('button[type="submit"]');
      if (button) {
        button.classList.add('is-loading');
        button.disabled = true;
      }

      request.finally(() => {
        // The re-render usually throws this button away.
        if (!button?.isConnected) return;
        button.classList.remove('is-loading');
        button.disabled = false;
      });
    }

    onDocumentClick(event) {
      if (event.defaultPrevented || !this.isActive()) return;
      const link = event.target.closest?.('a[href]');
      if (!link || this.contains(link)) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== this.dataset.ccdUrl) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      } else if (event.key === 'Enter' && event.target.matches?.('[data-ccd-action="set"]')) {
        // The quantity input belongs to the checkout form, so Enter would otherwise submit to checkout.
        event.preventDefault();
        event.target.dataset.submittedValue = event.target.value;
        this.changeLine(event.target.dataset.ccdKey, event.target.value);
      }
    }
  }

  customElements.define('custom-cart-drawer', CustomCartDrawer);

  const drawerElement = () => document.querySelector('custom-cart-drawer');

  // The section renders these from the cart it was built with, and render() keeps them current.
  function drawerCart() {
    const content = document.querySelector('[data-ccd-content]');
    if (!content) return null;
    const total = Number(content.dataset.ccdTotal);
    if (!Number.isFinite(total)) return null;
    return {
      total,
      giftLine: content.dataset.ccdGiftLine || '',
      giftQuantity: Number(content.dataset.ccdGiftQuantity) || 0,
      giftExtra: (content.dataset.ccdGiftExtra || '').split(',').filter(Boolean),
    };
  }

  /*
    Anything on the site can follow the free shipping threshold with a bare marker:

      <span data-free-shipping-text>FREE Shipping +$55</span>

    Both wordings live on the cart drawer section. Whatever a section puts inside the
    marker is only the no-JS fallback.
  */
  const FREE_SHIPPING = '[data-free-shipping-text]';

  const freeShippingText = {
    timer: 0,

    settings() {
      const drawer = drawerElement();
      if (!drawer) return null;
      const threshold = Number(drawer.dataset.ccdShippingThreshold);
      const locked = drawer.dataset.ccdShippingLocked;
      const unlocked = drawer.dataset.ccdShippingUnlocked;
      if (!threshold || !locked || !unlocked) return null;
      return { threshold, locked, unlocked };
    },

    apply(total) {
      const settings = this.settings();
      if (!settings || !Number.isFinite(total)) return;
      const text = total >= settings.threshold ? settings.unlocked : settings.locked;
      for (const element of document.querySelectorAll(FREE_SHIPPING)) {
        if (element.textContent.trim() !== text) element.textContent = text;
      }
    },

    // A hidden arm keeps the text Liquid gave it, so an inactive test costs no request.
    isOnScreen() {
      return [...document.querySelectorAll(FREE_SHIPPING)].some((element) => element.getClientRects().length > 0);
    },

    applyKnown() {
      const known = drawerCart();
      if (known) this.apply(known.total);
    },

    async read() {
      // A switched-on drawer has already re-rendered itself with the new cart.
      if (drawerElement()?.isActive?.()) return;
      if (!this.isOnScreen()) return;
      try {
        const base = drawerElement()?.dataset.ccdUrl || '/cart';
        const response = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        this.apply(Number((await response.json()).total_price));
      } catch {
        // Keep whatever Liquid rendered.
      }
    },

    schedule() {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.read(), 250);
    },

    start() {
      if (!document.querySelector(FREE_SHIPPING)) return;
      this.applyKnown();
      document.addEventListener('custom-cart-drawer:render', () => this.applyKnown());
      // With the drawer off nothing re-renders, so watch the requests instead.
      if (!('PerformanceObserver' in window)) return;
      const observer = new PerformanceObserver((list) => {
        const changed = list.getEntries().some((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
          return CART_MUTATION.test(new URL(entry.name).pathname);
        });
        if (changed) this.schedule();
      });
      observer.observe({ type: 'resource' });
    },
  };

  /*
    The gift is a real line the shopper can remove, so it goes in when the cart qualifies
    and comes out when it stops. Remove it by hand and it stays out until the cart drops
    below the threshold and climbs back. Never runs while the Slide Cart app owns the cart.
  */
  const GIFT_DECLINED = 'ccd-gift-declined';

  const freeGift = {
    present: false,
    busy: false,
    failed: false,

    settings() {
      const drawer = drawerElement();
      if (!drawer?.isActive?.()) return null;
      const variant = drawer.dataset.ccdGiftVariant;
      const threshold = Number(drawer.dataset.ccdGiftThreshold);
      if (!variant || !threshold) return null;
      return { drawer, variant, threshold };
    },

    declined(value) {
      if (value !== undefined) document.documentElement.toggleAttribute('data-ccd-gift-declined', value);
      try {
        if (value === undefined) return window.sessionStorage.getItem(GIFT_DECLINED) === '1';
        if (value) window.sessionStorage.setItem(GIFT_DECLINED, '1');
        else window.sessionStorage.removeItem(GIFT_DECLINED);
      } catch {
        // Private mode: the gift just comes back on the next qualifying page.
      }
      return false;
    },

    // Our own write re-renders the drawer while busy is still set, so that render is
    // skipped and this is the only place present gets caught up. Without it, the next
    // removal looks like a cart that never had the gift, and it goes straight back in.
    async run(request) {
      this.busy = true;
      try {
        if ((await request()) === null) this.failed = true;
      } finally {
        this.busy = false;
        this.present = Boolean(drawerCart()?.giftLine);
      }
    },

    async evaluate() {
      if (this.busy || this.failed) return;
      const settings = this.settings();
      const cart = drawerCart();
      if (!settings || !cart) return;

      const wasPresent = this.present;
      this.present = Boolean(cart.giftLine);

      if (cart.total < settings.threshold) {
        this.declined(false);
        if (cart.giftLine) await this.run(() => this.removeLines(settings.drawer, [cart.giftLine, ...cart.giftExtra]));
        return;
      }

      if (cart.giftLine) {
        // Two tabs can each add one, and an add still in flight when the shopper changes page
        // is repeated on the next one; there is no stepper to fix it by hand.
        if (cart.giftExtra.length || cart.giftQuantity > 1) {
          await this.run(async () => {
            if ((await this.removeLines(settings.drawer, cart.giftExtra)) === null) return null;
            return cart.giftQuantity > 1 ? settings.drawer.changeLine(cart.giftLine, 1, true) : true;
          });
        }
        return;
      }

      // Qualifies but the gift is gone: they removed it. It stays out, and the drawer
      // offers it back as the first upsell.
      if (wasPresent) {
        this.declined(true);
        return;
      }

      if (this.declined()) return;
      await this.run(() => settings.drawer.addVariant(settings.variant, 1, true));
    },

    // Sequential: each change re-renders the cart the next one is addressed against.
    async removeLines(drawer, keys) {
      for (const key of keys) {
        if ((await drawer.changeLine(key, 0, true)) === null) return null;
      }
      return true;
    },

    start() {
      this.declined(this.declined());
      this.evaluate();
      document.addEventListener('custom-cart-drawer:render', () => this.evaluate());
    },
  };

  freeShippingText.start();
  freeGift.start();

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = window.CustomCartDrawer;
    if (!drawer || event.detail.sectionId !== drawer.dataset.sectionId) return;
    // Preview only: lets the editor show the drawer without a test running.
    document.documentElement.dataset.customCartDrawer = 'on';
    drawer.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = window.CustomCartDrawer;
    if (drawer && event.detail.sectionId === drawer.dataset.sectionId) drawer.close();
  });
})();
